import { getDeviceUuid, getStoreReviewCtaEligibility, recordStoreReviewCtaEvent } from '../api';

const FIRST_SEEN_KEY = 'store_review_first_seen_at';
const ACTION_COUNT_KEY = 'store_review_medication_action_count';
const LAST_ATTEMPT_KEY = 'store_review_last_attempted_at';
const LAST_DISMISSED_KEY = 'store_review_last_dismissed_at';
const ATTEMPT_COUNT_KEY = 'store_review_attempt_count';
const COMPLETED_KEY = 'store_review_completed';
const RECAP_COUNTED_PREFIX = 'store_review_recap_counted_';
const APP_RUNTIME_INFO_KEY = 'pill_reminder_app_runtime_info';
const SERVER_ELIGIBILITY_KEY = 'store_review_server_eligibility';

const COOLDOWN_DAYS = 14;
const MAX_ATTEMPTS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;
const ELIGIBILITY_CACHE_MS = 5 * 60 * 1000;
const FORCE_ELIGIBLE_KEY = 'store_review_force_eligible';

function readDate(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? null : date;
    } catch {
        return null;
    }
}

function writeNow(key) {
    try {
        localStorage.setItem(key, new Date().toISOString());
    } catch {}
}

function readNumber(key) {
    try {
        return Number(localStorage.getItem(key) || 0);
    } catch {
        return 0;
    }
}

function daysSince(date) {
    if (!date) return Infinity;
    return (Date.now() - date.getTime()) / DAY_MS;
}

function supportsNativeStoreReview() {
    try {
        return readRuntimeInfo().capabilities?.storeReview === true;
    } catch {
        return false;
    }
}

function readRuntimeInfo() {
    try {
        return JSON.parse(localStorage.getItem(APP_RUNTIME_INFO_KEY) || 'null') || {};
    } catch {
        return {};
    }
}

function isLocalDevHost() {
    try {
        const host = window.location.hostname;
        return host === 'localhost' ||
            host === '127.0.0.1' ||
            host.startsWith('100.') ||
            host.startsWith('172.') ||
            host.startsWith('192.168.');
    } catch {
        return false;
    }
}

function isForceEligibleEnabled() {
    try {
        const runtimeInfo = readRuntimeInfo();
        const isDevRuntime = runtimeInfo.isDev === true || isLocalDevHost();
        if (!isDevRuntime) return false;
        return localStorage.getItem(FORCE_ELIGIBLE_KEY) === '1';
    } catch {
        return false;
    }
}

function clearServerEligibilityCache() {
    try {
        localStorage.removeItem(SERVER_ELIGIBILITY_KEY);
    } catch {}
}

export function resetStoreReviewLocalState() {
    try {
        Object.keys(localStorage)
            .filter(key => key.startsWith('store_review'))
            .forEach(key => localStorage.removeItem(key));
    } catch {}
}

function readCachedServerEligibility() {
    try {
        const raw = localStorage.getItem(SERVER_ELIGIBILITY_KEY);
        if (!raw) return null;
        const cached = JSON.parse(raw);
        if (!cached?.checkedAt || Date.now() - cached.checkedAt > ELIGIBILITY_CACHE_MS) return null;
        return cached.value || null;
    } catch {
        return null;
    }
}

function writeCachedServerEligibility(value) {
    try {
        localStorage.setItem(SERVER_ELIGIBILITY_KEY, JSON.stringify({
            checkedAt: Date.now(),
            value,
        }));
    } catch {}
}

async function fetchServerEligibility() {
    const cached = readCachedServerEligibility();
    if (cached) return cached;

    const eligibility = await getStoreReviewCtaEligibility(getDeviceUuid());
    writeCachedServerEligibility(eligibility);
    return eligibility;
}

function recordStoreReviewCtaEventSafe(eventType, source = 'unknown') {
    try {
        const runtimeInfo = readRuntimeInfo();
        recordStoreReviewCtaEvent(getDeviceUuid(), {
            eventType,
            source,
            platform: runtimeInfo.platform || window.NATIVE_PLATFORM || null,
            appVersion: runtimeInfo.appVersion || null,
            runtimeVersion: runtimeInfo.runtimeVersion || null,
            channel: runtimeInfo.channel || null,
        }).catch(() => {});
    } catch {}
}

export function ensureStoreReviewFirstSeen() {
    try {
        if (!localStorage.getItem(FIRST_SEEN_KEY)) writeNow(FIRST_SEEN_KEY);
    } catch {}
}

export function recordMedicationReviewEligibleAction(amount = 1) {
    ensureStoreReviewFirstSeen();
    try {
        const next = readNumber(ACTION_COUNT_KEY) + amount;
        localStorage.setItem(ACTION_COUNT_KEY, String(next));
        clearServerEligibilityCache();
        return next;
    } catch {
        clearServerEligibilityCache();
        return readNumber(ACTION_COUNT_KEY);
    }
}

export function recordMonthlyRecapReviewEligibleAction(periodKey, amount = 0) {
    if (!periodKey || amount <= 0) return readNumber(ACTION_COUNT_KEY);
    const countedKey = `${RECAP_COUNTED_PREFIX}${periodKey}`;
    try {
        if (localStorage.getItem(countedKey) === '1') return readNumber(ACTION_COUNT_KEY);
        localStorage.setItem(countedKey, '1');
    } catch {
        return readNumber(ACTION_COUNT_KEY);
    }
    return recordMedicationReviewEligibleAction(amount);
}

export async function canShowStoreReviewPrompt(source = 'unknown') {
    ensureStoreReviewFirstSeen();
    if (!window.ReactNativeWebView) return false;
    if (localStorage.getItem(COMPLETED_KEY) === '1') return false;
    if (isForceEligibleEnabled() && source === 'medication_action') return true;
    if (!supportsNativeStoreReview()) return false;
    if (readNumber(ATTEMPT_COUNT_KEY) >= MAX_ATTEMPTS) return false;

    const lastAttemptedAt = readDate(LAST_ATTEMPT_KEY);
    const lastDismissedAt = readDate(LAST_DISMISSED_KEY);
    const lastTouchedAt = [lastAttemptedAt, lastDismissedAt]
        .filter(Boolean)
        .sort((a, b) => b.getTime() - a.getTime())[0];

    if (daysSince(lastTouchedAt) < COOLDOWN_DAYS) return false;

    try {
        const eligibility = await fetchServerEligibility();
        return eligibility?.eligible === true;
    } catch {
        return false;
    }
}

export function recordStoreReviewDismissed(source = 'unknown') {
    writeNow(LAST_DISMISSED_KEY);
    recordStoreReviewCtaEventSafe('dismissed', source);
}

export function recordStoreReviewShown(source = 'unknown') {
    writeNow(LAST_ATTEMPT_KEY);
    recordStoreReviewCtaEventSafe('shown', source);
}

export function requestNativeStoreReview(source = 'unknown') {
    if (!window.ReactNativeWebView) return false;
    window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'REQUEST_STORE_REVIEW',
        source,
    }));

    try {
        localStorage.setItem(COMPLETED_KEY, '1');
        localStorage.setItem(ATTEMPT_COUNT_KEY, String(readNumber(ATTEMPT_COUNT_KEY) + 1));
    } catch {}
    recordStoreReviewCtaEventSafe('review_clicked', source);
    return true;
}
